import React from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import { LuShoppingBasket } from "react-icons/lu";
import { LuDoorOpen } from "react-icons/lu";
import { FaSquareParking } from "react-icons/fa6";
import SearchBar from "./SearchBar";
import IconButton from "./IconButton";
import { IoPencil } from "react-icons/io5";
import { FaList } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Properties from "./Properties";
import Products from "./Products";
import Parking from "./Parking";
import { useCompany } from "../../../context/CompanyContext";

export default function Marketplace() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const searchInputRef = React.useRef(null);
  const searchContainerRef = React.useRef(null);

  const { getCapitalizedTerm, companyData } = useCompany();

  const categories = [
    {
      id: 1,
      name: "Products/Services",
      icon: <LuShoppingBasket className="text-base" />,
    },
    {
      id: 2,
      name: `${getCapitalizedTerm("assets")} for Sale/Long Term Leases`,
      icon: <LuDoorOpen className="text-base" />,
    },
    ...(companyData?.entityType !== "hoa"
      ? [
          {
            id: 3,
            name: "Parking Space",
            icon: <FaSquareParking className="text-base" />,
          },
        ]
      : []),
  ];

  // If we're on HOA and activeCategory is 3, reset to 1
  React.useEffect(() => {
    if (companyData?.entityType === "hoa" && activeCategory === 3) {
      setActiveCategory(1);
    }
  }, [companyData?.entityType, activeCategory]);

  const handleAddMode = () => {
    if (activeCategory === 2) {
      navigate("/new-listing-property");
    }
    if (activeCategory === 1) {
      navigate("/new-product-service");
    }
    if (activeCategory === 3) {
      navigate("/new-parking-space");
    }
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    // Focus the input field when opened
    if (!isSearchOpen) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  React.useEffect(() => {
    // Function to check if clicked outside of the search component
    const handleClickOutside = (event) => {
      if (
        isSearchOpen &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target) &&
        searchTerm === ""
      ) {
        setIsSearchOpen(false);
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearchOpen, searchTerm]);

  return (
    <UserScreenWindow label="Marketplace">
      <div className="w-full mx-auto px-4">
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaList className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Marketplace
              </h1>
              <p className="text-xs text-gray-500">
                Browse and list items for sale
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6 -mx-4 relative">
          <div className="overflow-x-auto px-4 pr-20 flex no-scrollbar relative">
            <div className="flex space-x-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`flex items-center whitespace-nowrap px-4 py-2 rounded-full text-xs font-medium transition-all ${
                    activeCategory === category.id
                      ? "bg-[#363062] text-white shadow-sm"
                      : "bg-gray-50 text-[#363062] hover:bg-[#363062]/5"
                  }`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.icon}
                  <span className="ml-2">{category.name}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-gray-100 via-gray-100 to-transparent pointer-events-none"></div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-shrink-0">
              <IconButton
                icon={<IoPencil />}
                label="Create Listing"
                onClick={handleAddMode}
                className="bg-[#8FCD00] text-white hover:bg-[#8FCD00]/90 transition-colors whitespace-nowrap"
              />
            </div>

            <div ref={searchContainerRef} className="relative">
              <AnimatePresence>
                {isSearchOpen ? (
                  <motion.div
                    className="relative"
                    initial={{ width: 40, opacity: 0 }}
                    animate={{ width: "200px", opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="w-full py-2 px-4 pr-10 text-xs bg-gray-50 rounded-full border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
                      placeholder={`Search ${
                        categories.find((cat) => cat.id === activeCategory)
                          ?.name || ""
                      }`}
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#363062]"
                      onClick={toggleSearch}
                    >
                      <FiSearch className="text-sm" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    className="flex items-center justify-center w-10 h-10 p-2 rounded-full bg-[#363062]/10 hover:bg-[#363062]/20 text-[#363062] transition-colors"
                    onClick={toggleSearch}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <FiSearch className="text-base" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {activeCategory === 2 && (
              <Properties searchTerm={searchTerm} statusFilter="active" />
            )}
            {activeCategory === 1 && (
              <Products searchTerm={searchTerm} statusFilter="active" />
            )}
            {activeCategory === 3 && companyData?.entityType !== "hoa" && (
              <Parking searchTerm={searchTerm} statusFilter="active" />
            )}
          </motion.div>
        </div>
      </div>
    </UserScreenWindow>
  );
}
